import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import Heading from 'components/partials/heading/heading';

import Icons from 'assets/icons';

import { unmountModal } from 'helpers/modal-render';

import { useModalFocusTrap } from 'components/partials/modal/use-modal-focus-trap';

const MODAL_WRAPPER_ATTR = 'data-modal-wrapper';
const MODAL_ID_ATTR = 'data-modal-id';

const ENTER_NATIVE_TAGS = new Set(['BUTTON', 'A', 'TEXTAREA', 'SELECT']);

const targetHandlesEnter = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;

  const role = target.getAttribute('role');

  return (
    ENTER_NATIVE_TAGS.has(target.tagName) ||
    role === 'button' ||
    (role === 'combobox' && target.getAttribute('aria-expanded') === 'true') ||
    target.isContentEditable
  );
};

export interface ModalKeyboardAction {
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
}

interface ModalProps {
  children?: React.ReactNode;
  onCloseCallback?: () => void;
  onConfirmCallback?: () => void | Promise<void>;
  headerText?: string;
  headerSubtext?: string;
  onConfirmButtonLabel?: string;
  disabled?: boolean;
  text?: string;
  onCloseModal?: () => void;
  disableBackdropClick?: boolean;
  formId?: string;
  modalClassName?: string;
  containerClassName?: string;
  backdropClassName?: string;
  footer?: React.ReactNode;
  stepIndicator?: React.ReactNode;
  loading?: boolean;
  showCloseButton?: boolean;
  skipUnmountOnClose?: boolean;
  disableEnterConfirm?: boolean;
  primaryAction?: ModalKeyboardAction;
  secondaryAction?: ModalKeyboardAction;
  'data-testid'?: string;
  confirmButtonTestId?: string;
  cancelButtonTestId?: string;
  headerClassName?: string;
  textClassName?: string;
  hasSectionSeparator?: boolean;
  ariaLabel?: string;
}

const Modal = ({
  children,
  onCloseCallback,
  onConfirmCallback,
  headerText,
  headerSubtext,
  onConfirmButtonLabel = 'OK',
  disabled,
  text,
  onCloseModal = () => {},
  disableBackdropClick = false,
  formId,
  modalClassName,
  containerClassName = '',
  backdropClassName,
  footer,
  stepIndicator,
  loading,
  showCloseButton = false,
  skipUnmountOnClose = false,
  disableEnterConfirm = false,
  primaryAction,
  secondaryAction,
  'data-testid': dataTestId = 'modal',
  confirmButtonTestId = 'confirm-modal-button',
  cancelButtonTestId = 'cancel-modal-button',
  headerClassName,
  textClassName,
  hasSectionSeparator = false,
  ariaLabel,
}: ModalProps): React.JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onClose = useCallback(() => {
    if (!skipUnmountOnClose) {
      unmountModal();
    }
    if (onCloseCallback) {
      onCloseCallback();
    }
  }, [onCloseCallback, skipUnmountOnClose]);

  useEffect(() => {
    const currentUrl = window.location.pathname + window.location.search;

    window.history.pushState(null, '', currentUrl);
    window.addEventListener('popstate', onClose);

    return () => {
      window.removeEventListener('popstate', onClose);
    };
  }, [onClose]);

  const onConfirm = useCallback(async () => {
    if (!onConfirmCallback) {
      onClose();

      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirmCallback();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirmCallback, onClose]);

  const modalWrapperRf = useRef<HTMLDivElement>(null);
  const modalInnerRf = useRef<HTMLDivElement>(null);

  const titleId = useId();
  const hasHeader = Boolean(headerText);

  const primaryDisabled = primaryAction
    ? !!primaryAction.disabled || !!primaryAction.loading
    : !!footer ||
      disableEnterConfirm ||
      !!disabled ||
      !!(loading || isSubmitting);
  const primaryHandler = primaryAction ? primaryAction.onClick : onConfirm;

  const secondaryDisabled = !!secondaryAction?.disabled;
  const secondaryHandler = secondaryAction ? secondaryAction.onClick : onClose;

  const primaryHandlerRef = useRef(primaryHandler);
  const primaryDisabledRef = useRef(primaryDisabled);
  const secondaryHandlerRef = useRef(secondaryHandler);
  const secondaryDisabledRef = useRef(secondaryDisabled);
  const formIdRef = useRef(formId);

  primaryHandlerRef.current = primaryHandler;
  primaryDisabledRef.current = primaryDisabled;
  secondaryHandlerRef.current = secondaryHandler;
  secondaryDisabledRef.current = secondaryDisabled;
  formIdRef.current = formId;

  const { modalId, isTopmost } = useModalFocusTrap(modalWrapperRf);

  const handleClickOutside = useCallback(
    (e: MouseEvent): void => {
      if (disableBackdropClick) return;
      if (!isTopmost()) return;
      const target = e.target as Node;

      if (!modalInnerRf.current?.contains(target)) {
        const clickedModalWrapper = (target as HTMLElement).closest?.(
          `[${MODAL_WRAPPER_ATTR}]`
        );

        if (
          clickedModalWrapper &&
          clickedModalWrapper !== modalWrapperRf.current
        ) {
          return;
        }

        onClose();
      }
    },
    [disableBackdropClick, isTopmost, onClose]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (!isTopmost()) return;

      if (e.defaultPrevented) return;

      if (
        e.target instanceof HTMLElement &&
        e.target.closest('[data-modal-keyboard-ignore]')
      ) {
        return;
      }

      if (e.key === 'Escape') {
        if (!secondaryDisabledRef.current) secondaryHandlerRef.current();
        if (skipUnmountOnClose) {
          e.stopPropagation();
          e.preventDefault();
        }
      } else if (
        e.key === 'Enter' &&
        !formIdRef.current &&
        !primaryDisabledRef.current
      ) {
        if (targetHandlesEnter(e.target)) return;

        e.preventDefault();
        primaryHandlerRef.current();
      }
    },
    [isTopmost, skipUnmountOnClose]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, skipUnmountOnClose);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener(
        'keydown',
        handleKeyDown,
        skipUnmountOnClose
      );
    };
  }, [handleClickOutside, handleKeyDown, skipUnmountOnClose]);

  const triggerElementRf = useRef<HTMLElement | null>(null);

  useEffect(() => {
    triggerElementRf.current = document.activeElement as HTMLElement | null;
    if (modalWrapperRf.current) {
      modalWrapperRf.current.focus();
    }

    return () => {
      triggerElementRf.current?.focus?.();
    };
  }, []);

  return (
    <div
      className={twMerge(
        'fixed inset-0 z-[701] h-[100vh] max-h-[100vh] overflow-hidden',
        containerClassName
      )}
      ref={modalWrapperRf}
      role="dialog"
      aria-modal="true"
      aria-labelledby={hasHeader ? titleId : undefined}
      aria-label={hasHeader ? undefined : ariaLabel ?? 'Dialog'}
      tabIndex={-1}
      data-testid="modal-wrapper"
      {...{ [MODAL_WRAPPER_ATTR]: true, [MODAL_ID_ATTR]: modalId }}
    >
      <div
        className={twMerge(
          'absolute inset-0 opacity-60 bg-navy-950',
          backdropClassName
        )}
      />
      <div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
        <div
          ref={modalInnerRf}
          className={twMerge(
            'mobile:w-[330px] tablet:w-[500px] bg-white p-8 rounded-md overflow-hidden flex flex-col justify-between relative',
            modalClassName
          )}
          data-testid={dataTestId}
        >
          {showCloseButton && (
            <IconButton
              variant="reserve-text"
              size="xs"
              icon={<Icons.Close />}
              onClick={onClose}
              aria-label="Close modal"
              className="absolute right-3 top-3"
              data-testid="close-modal-button"
            />
          )}
          <div className="flex flex-col items-start">
            <Heading
              variant="h1"
              id={hasHeader ? titleId : undefined}
              customClass={twMerge(
                'mobile:text-center tablet:text-left',
                headerClassName
              )}
            >
              {headerText}
            </Heading>
            {stepIndicator && (
              <span className="text-gray-500 text-sm shrink-0">
                {stepIndicator}
              </span>
            )}
          </div>
          {headerSubtext && (
            <p className="text-sm text-gray-600 mt-2 mb-3 mobile:text-center tablet:text-left">
              {headerSubtext}
            </p>
          )}
          {hasSectionSeparator && (
            <div className="border-b border-gray-200 -mx-8 mt-4" />
          )}
          {text && (
            <p
              className={twMerge(
                'text-lg mt-1 mobile:text-center tablet:text-left',
                textClassName
              )}
            >
              {text}
            </p>
          )}
          {children}
          {footer ? (
            <div className="mt-6">{footer}</div>
          ) : (
            <div className="flex mobile:flex-col-reverse tablet:flex-row justify-end mt-6 space-y-2 tablet:space-y-0 gap-x-2">
              <Button
                type="button"
                variant="reserve-outlined"
                onClick={() => {
                  onClose();
                  onCloseModal();
                }}
                data-testid={cancelButtonTestId}
              >
                Cancel
              </Button>
              <Button
                type={formId ? 'submit' : 'button'}
                form={formId}
                onClick={formId ? undefined : onConfirm}
                variant="brand-filled"
                disabled={disabled}
                loading={loading || isSubmitting}
                data-testid={confirmButtonTestId}
              >
                {onConfirmButtonLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
